import { NgClass, NgFor, NgIf } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  Inject,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  Validators,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatSelect } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { finalize, tap } from 'rxjs';
import { TableComponent } from '../../custom-table/table.component';
import { InputComponent } from '../../from-input/input.component';
import { CustomToastComponent } from '../../toast/custom-toast.component';
import { AddSchoolComponent } from '../add-school/add-school.component';
import { CommonDialogComponent } from '../commom/commom-dialog.component';
import { ApiError, HttpService } from './../../../services/http.service';
import * as XLSX from 'xlsx';
interface APIResponse {
  schoolId: string;
  schoolName: string;
  address: string;
  suburb: string;
  postcode: string;
  teacherId: string;
  studentId: string;
  firstName: string;
  lastName: string;
  email: string;
  mobileNumber: string;
  isActive: boolean | string;
  dateOfBirth: string;
  assignedTeacher: string;
  name: string;
  id: string;
}

interface ApiDataResponse {
  status: boolean;
  message: string;
  data: APIResponse | APIResponse[];
}

export interface BulkUploadDialogData {
  title: string;
  schools: string[];
  confirmText?: string;
  cancelText?: string;
  type?: 'student' | 'teacher' | string;
}

@Component({
  selector: 'app-bulk-upload-dialog',
  templateUrl: './addBulk-dialog.component.html',
  styleUrls: ['./addBulk-dialog.component.scss'],
  imports: [
    NgIf,
    InputComponent,
    NgFor,
    NgClass,
    MatTooltipModule,
    FormsModule,
  ],
})
export class AddBulkDialogComponent {
  @ViewChild('schoolSelect') schoolSelect: MatSelect | undefined;
  @ViewChild(TableComponent) tableComponent!: TableComponent;
  @ViewChild('actionButtons') actionButtonsTemplate!: TemplateRef<any>;
  @ViewChild('addSchoolContent') addSchoolContent!: TemplateRef<any>;
  schoolControl = new FormControl('', Validators.required);
  fileControl = new FormControl('', Validators.required);
  form!: FormGroup;
  uploadedFileName: string = '';
  schoolOptions: any[] = [];
  currentStep: 'upload' | 'review' | 'submit' = 'upload';
  teacherColumns: string[] = ['firstName', 'lastName', 'email', 'phoneNumber'];
  studentColumns: string[] = [
    'firstName',
    'lastName',
    'email (Personal)',
    'dateOfBirth',
    'gender',
    'ethnicity',
    'mobileNumber',
    'postcode',
    'email (School)',
    'assign teacher email',
  ];
  teacherTabledata: any[] = [];
  studentTabledata: any[] = [];
  stepOrder: string[] = ['upload', 'review', 'submit'];
  uploadedFiles: File[] = [];
  selectedRows: any[] = [];
  validateData: any = null;
  loading = false;
  editingSchool: any = null;
  type: string = 'student';
  noteText: string = '';
  teacherMode: boolean = false;
  showConfirmationMessage: boolean = false;
  submitting: boolean = false;
  isDragOver: boolean = false;
  downloadTemplateLoading: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<AddBulkDialogComponent>,
    private httpService: HttpService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    @Inject(MAT_DIALOG_DATA) public data: BulkUploadDialogData,
  ) {
    const role = localStorage.getItem('userRole');
    this.teacherMode = role === 'teacher' ? true : false;
  }
  private addSchoolDialogRef!: MatDialogRef<CommonDialogComponent>;

  ngOnInit(): void {
    this.form = new FormGroup({
      schoolName: this.schoolControl,
    });
    if (!this.teacherMode) {
      this.fetchSchoolsDropDown();
    }
    this.type = this.data.type || 'student';
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFiles(files);
    }
  }

  private handleFiles(fileList: FileList): void {
    const newFiles = Array.from(fileList);
    const hasInvalidFile = newFiles.some(
      (file) =>
        !(
          file.type === 'text/csv' ||
          file.name.toLowerCase().endsWith('.csv') ||
          file.type ===
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
          file.name.toLowerCase().endsWith('.xlsx')
        ),
    );

    if (hasInvalidFile) {
      this.fileControl.setErrors({ invalidType: true });
      this.fileControl.markAsTouched();
      this.showErrorToast('Please upload only CSV or Excel files.');
      return;
    }

    this.uploadedFiles.push(...newFiles);
    this.fileControl.setValue('uploaded');
    this.fileControl.setErrors(null);
    this.fileControl.markAsTouched();

    newFiles.forEach((file) => {
      const fileName = file.name.toLowerCase();

      if (fileName.endsWith('.xlsx')) {
        const reader = new FileReader();
        reader.onload = (e: any) => {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const csvData = XLSX.utils.sheet_to_csv(firstSheet);
          this.parseCSV(csvData);
        };
        reader.readAsArrayBuffer(file);
      } else {
        const reader = new FileReader();
        reader.onload = () => {
          const text = reader.result as string;
          this.parseCSV(text);
        };
        reader.readAsText(file);
      }
    });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.handleFiles(input.files);
    }
    input.value = '';
  }

  parseXLSX(result: string | ArrayBuffer | null) {
    throw new Error('Method not implemented.');
  }

  removeFile(index: number): void {
    this.uploadedFiles.splice(index, 1);
    if (this.uploadedFiles.length === 0) {
      this.fileControl.setValue('');
      this.fileControl.setErrors({ required: true });
      if (this.type === 'teacher') {
        this.teacherTabledata = [];
      } else {
        this.studentTabledata = [];
      }
    } else {
      this.fileControl.setValue('uploaded');
      this.fileControl.setErrors(null);
      if (this.type === 'teacher') {
        this.teacherTabledata = [];
      } else {
        this.studentTabledata = [];
      }
    }
    this.fileControl.markAsTouched();
  }

  parseCSV(csvText: string): void {
    const lines = csvText.split('\n').filter((line) => line.trim() !== '');
    if (lines.length < 2) return;

    const headers = lines[0].split(',').map((h) => h.trim().toLowerCase());

    function getHeaderIndex(possibilities: string[]): number {
      for (const key of possibilities) {
        const idx = headers.indexOf(key.toLowerCase());
        if (idx !== -1) return idx;
      }
      return -1;
    }

    const required =
      this.type === 'teacher'
        ? ['first name', 'last name']
        : [
            'first name',
            'last name',
            'email (personal)',
            ['dateofbirth', 'date of birth', 'dob', 'date_of_birth'],
            'gender',
            'ethnicity',
            'email (school)',
          ];

    const indices: { [key: string]: number } = {};
    for (const header of required) {
      if (Array.isArray(header)) {
        indices[header[0]] = getHeaderIndex(header);
      } else {
        indices[header] = headers.indexOf(header.toLowerCase());
      }
    }

    const missing = required
      .map((header) => (Array.isArray(header) ? header[0] : header))
      .filter((header) => indices[header] === -1);

    if (missing.length) {
      console.error('Missing required headers:', missing);
      this.showErrorToast(`Missing required headers: ${missing.join(', ')}`);
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    const data = lines.slice(1).map((line, index) => {
      const values = line.split(',').map((v) => v.trim());
      let isValid = true;

      if (this.type === 'teacher') {
        const email = values[indices['email']] || '';
        const phone = values[indices['phone number']] || '';

        if (!emailRegex.test(email)) isValid = false;
        if (!/^\d+$/.test(phone)) isValid = false;

        return {
          firstName: values[indices['first name']] || '',
          lastName: values[indices['last name']] || '',
          email,
          phoneNumber: phone,
          isValid,
        };
      } else {
        let dateOfBirth = '';
        if (indices['dateofbirth'] !== -1)
          dateOfBirth = values[indices['dateofbirth']] || '';
        const emailPersonal = values[indices['email (personal)']] || '';
        const emailSchool = values[indices['email (school)']] || '';
        const mobile = values[indices['mobile number']] || '';
        const teacherEmail = values[indices['assign teacher email']] || '';

        if (!emailRegex.test(emailPersonal)) isValid = false;
        if (!emailRegex.test(teacherEmail)) isValid = false;
        if (!emailRegex.test(emailSchool)) isValid = false;
        if (!/^\d+$/.test(mobile)) isValid = false;

        return {
          firstName: values[indices['first name']] || '',
          lastName: values[indices['last name']] || '',
          'email (Personal)': emailPersonal,
          dateOfBirth: dateOfBirth || '',
          gender: values[indices['gender']] || '',
          ethnicity: values[indices['ethnicity']] || '',
          mobileNumber: mobile,
          postcode: values[indices['post code']] || '',
          'email (School)': emailSchool,
          'assign teacher email': teacherEmail || '',
          isValid,
        };
      }
    });

    if (this.type === 'teacher') {
      this.teacherTabledata = data;
    } else {
      this.studentTabledata = data;
    }
  }

  fetchSchoolsDropDown(): void {
    this.loading = true;
    this.httpService
      .get<ApiDataResponse>(`Schools/dropdown`)
      .pipe(
        tap({
          next: (response) => {
            if (Array.isArray(response.data)) {
              this.schoolOptions = response.data.map((item) => ({
                label: item.name,
                value: item.id,
              }));
            } else {
              this.schoolOptions = [];
              this.showErrorToast('Invalid school data format');
            }
          },
          error: (err) => {
            console.error('Failed to load school details:', err);
            this.schoolOptions = [];
            this.showErrorToast(err?.message);
          },
        }),
        finalize(() => {
          this.loading = false;
        }),
      )
      .subscribe();
  }

  onFileDropped(file: File): void {
    this.uploadedFileName = file.name;
  }

  onAddNewSchool(
    schoolToEdit: any = null,
    isEdit: boolean = false,
    width: string = '50.5rem',
  ): void {
    this.editingSchool = schoolToEdit || null;
    const dialogRef = this.dialog.open(AddSchoolComponent, {
      width: width,
      maxWidth: '90vw',
      minWidth: '300px',
      data: { initialData: schoolToEdit, EditMode: isEdit },
      disableClose: true,
      autoFocus: false,
      panelClass: 'custom-dialog-container',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        // this.fetchSchools();
      }
      this.editingSchool = null;
    });
  }

  onNoteChange() {
    // console.log('Note entered:', this.noteText);
  }

  autoResize(event: Event) {
    const textarea = event.target as HTMLTextAreaElement;
    textarea.style.height = 'auto';
    textarea.style.height = textarea.scrollHeight + 'px';
  }

  shouldShowErrorMargin(): boolean {
    return !!(
      this.schoolControl?.invalid &&
      this.schoolControl?.touched &&
      this.schoolControl?.errors
    );
  }

  onConfirm(): void {
    if (this.submitting) {
      return;
    }

    if (this.currentStep === 'upload') {
      this.fileControl.markAsTouched();

      if (!this.teacherMode && this.type !== 'teacher') {
        this.schoolControl.markAsTouched();
      }
      if (this.fileControl.invalid || this.uploadedFiles.length === 0) {
        return;
      }

      if (
        !this.teacherMode &&
        this.schoolControl.invalid &&
        this.type !== 'teacher'
      ) {
        return;
      }

      this.submitting = true;
      const endpoint = this.teacherMode
        ? 'teacherstudent/bulk-upload/validate'
        : this.type === 'teacher'
          ? 'teachers/bulk-upload/validate'
          : 'students/bulk-upload/validate';
      const selectedSchoolId = this.schoolControl.value;
      const formData = new FormData();

      if (this.type === 'student') {
        formData.append('schoolId', selectedSchoolId ?? '');
      }

      this.uploadedFiles.forEach((file, index) => {
        formData.append('file', file);
      });

      this.httpService
        .post<ApiDataResponse>(endpoint, formData, true)
        .pipe(
          finalize(() => {
            this.submitting = false;
          }),
        )
        .subscribe({
          next: (response) => {
            if (response?.status) {
              this.showSuccessToast(response?.message);
              this.validateData = response.data;
              this.currentStep = 'review';
            } else {
              this.showErrorToast(response?.message);
            }
          },
          error: (err) => {
            const apiError = err as ApiError;
            this.showErrorToast(err?.message || 'Upload failed');
          },
        });
    } else if (this.currentStep === 'review') {
      if (this.teacherMode) {
        this.showConfirmationMessage = true;
        this.currentStep = 'submit';
      } else {
        this.submitData();
      }
    } else if (this.currentStep === 'submit' && this.showConfirmationMessage) {
      this.submitData();
    }
  }

  private submitData(): void {
    if (this.submitting) {
      return;
    }

    this.submitting = true;
    const selectedSchoolId = this.schoolControl.value;
    let payload: any;
    let endpoint: string;

    if (this.type === 'teacher') {
      if (this.teacherMode) {
        const studentDetails = this.validateData?.validRecords.map(
          (row: {
            firstName: string;
            lastName: string;
            schoolEmail: string;
            dateOfBirth: string;
            yearLevel: number;
            parentGuardianFullName: string;
            parentGuardianEmail: string;
            gender: string;
            ethnicity: string;
          }) => ({
            FirstName: row.firstName,
            LastName: row.lastName,
            schoolEmail: row.schoolEmail,
            dateOfBirth: row.dateOfBirth,
            yearLevel: row.yearLevel,
            parentGuardianFullName: row?.parentGuardianFullName,
            parentGuardianEmail: row?.parentGuardianEmail,
            gender: row?.gender,
            ethnicity: row?.ethnicity,
          }),
        );
        payload = {
          notes: this.noteText || '',
          parentGuardianConsent: true,
          studentDetails,
        };
        endpoint = 'teacherstudent/bulk-upload';
      } else {
        payload = this.validateData?.validRecords.map(
          (row: {
            firstName: string;
            lastName: string;
            email: string;
            mobileNumber: string;
            schoolId: string;
          }) => ({
            FirstName: row.firstName,
            LastName: row.lastName,
            Email: row.email,
            MobileNumber: row.mobileNumber,
            SchoolId: row.schoolId,
          }),
        );
        endpoint = 'teachers/bulk-upload';
      }
    } else {
      const studentDetails = this.validateData?.validRecords.map(
        (row: {
          firstName: string;
          lastName: string;
          schoolEmail: string;
          dateOfBirth: string;
          yearLevel: number;
          parentGuardianFullName: string;
          parentGuardianEmail: string;
          gender: string;
          ethnicity: string;
          personalEmail: string;
        }) => ({
          FirstName: row.firstName,
          LastName: row.lastName,
          schoolEmail: row.schoolEmail,
          dateOfBirth: row.dateOfBirth,
          yearLevel: row.yearLevel,
          parentGuardianFullName: row?.parentGuardianFullName,
          parentGuardianEmail: row?.parentGuardianEmail,
          gender: row?.gender,
          ethnicity: row?.ethnicity,
          personalEmail: row?.personalEmail,
        }),
      );

      payload = [
        {
          SchoolId: selectedSchoolId,
          studentDetails,
        },
      ];
      endpoint = 'students/bulk-upload';
    }

    if (this.teacherMode && !endpoint.includes('teacherstudent')) {
      endpoint =
        this.type === 'teacher'
          ? 'teachers/bulk-upload'
          : 'students/bulk-upload';
    }

    this.httpService
      .post<ApiDataResponse>(endpoint, payload)
      .pipe(
        finalize(() => {
          this.submitting = false;
        }),
      )
      .subscribe({
        next: (response) => {
          if (response?.status) {
            this.showSuccessToast(response?.message);
            this.showConfirmationMessage = false;
            this.currentStep = 'submit';
            this.autoCloseDialog();
          } else {
            this.showErrorToast(response?.message ?? 'Upload failed');
            console.error('Upload error response:', response);
          }
        },
        error: (err) => {
          console.error('Request error:', err);
          const apiError = err as ApiError;
          this.showErrorToast(apiError?.message || 'Upload failed');
        },
      });
  }

  private autoCloseDialog(): void {
    if (this.currentStep === 'submit' && !this.showConfirmationMessage) {
      setTimeout(() => {
        this.dialogRef.close(true);
      }, 3000);
    }
  }

  getValidationErrorKeys(errors: any): string[] {
    return errors ? Object.keys(errors) : [];
  }

  downloadTemplate(): void {
    if (this.downloadTemplateLoading) return; // Block multiple clicks

    if (this.teacherMode) {
      this.downloadTemplateLoading = true;

      this.httpService
        .download(`teacherstudent/bulk-upload/read-format`)
        .subscribe({
          next: (res) => {
            const blob = res.body;
            if (!blob) {
              this.showErrorToast('Empty file received');
              return;
            }

            const contentDisposition =
              res.headers.get('content-disposition') || '';
            const filename =
              this.extractFilename(contentDisposition) ||
              'StudentBulkUploadReadFormat.xlsx';

            this.triggerDownload(blob, filename);
            this.showSuccessToast('Template downloaded successfully!');
          },
          error: (err) => {
            this.showErrorToast(err?.message || 'Failed to download template');
          },
          complete: () => {
            this.downloadTemplateLoading = false;
          },
        });
    } else {
      let templatePath: string;
      this.downloadTemplateLoading = true;
      this.httpService
        .download(
          this.type === 'teacher'
            ? `teachers/bulk-upload/read-format`
            : `students/bulk-upload/read-format`,
        )
        .subscribe({
          next: (res) => {
            const blob = res.body;
            if (!blob) {
              this.showErrorToast('Empty file received');
              return;
            }

            const contentDisposition =
              res.headers.get('content-disposition') || '';
            const filename =
              this.extractFilename(contentDisposition) ||
              'StudentBulkUploadReadFormat.xlsx';

            this.triggerDownload(blob, filename);
            this.showSuccessToast('Template downloaded successfully!');
          },
          error: (err) => {
            this.showErrorToast(err?.message || 'Failed to download template');
            this.downloadTemplateLoading = false;
          },
          complete: () => {
            this.downloadTemplateLoading = false;
          },
        });
    }
  }

  private extractFilename(cd: string): string | null {
    const match = /filename\*?=(?:UTF-8'')?["']?([^"';]+)/i.exec(cd);
    return match ? decodeURIComponent(match[1]) : null;
  }

  private triggerDownload(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  private triggerStaticDownload(path: string): void {
    const a = document.createElement('a');
    a.href = path;
    a.download = path.split('/').pop() || 'template';
    a.click();
  }

  getCombinedErrors(errors: any): string {
    const keys = Object.keys(errors || {});
    return keys.map((key) => errors[key]).join(', ');
  }

  private showSuccessToast(message: string): void {
    this.snackBar.openFromComponent(CustomToastComponent, {
      data: { message },
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['no-padding-snackbar', 'success-toast'],
    });
  }

  private showErrorToast(message: string | null | undefined): void {
    if (!message) return;
    this.snackBar.openFromComponent(CustomToastComponent, {
      data: { message },
      duration: 300000000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['no-padding-snackbar', 'error-toast'],
    });
  }

  isStepActive(step: 'upload' | 'review' | 'submit'): boolean {
    return (
      this.stepOrder.indexOf(this.currentStep) >= this.stepOrder.indexOf(step)
    );
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }
}
